import {useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";

import {castRtkErrorForFormik} from "@pg-mono/react-hook-forms-utils";
import {getPhoneDataFromString} from "@pg-mono/string-utils";

import {resetRecommendedOffers} from "../../../offer/detail/actions/fetch_recommended_offers";
import {IRecommendedOffer} from "../../../offer/types/IRecommendedOffer";
import {resetRecommendedProperties} from "../../../property/actions/fetch_recommended_properties";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {useRegionEstimatedPrice} from "../../../region/hooks/use_region_estimated_price";
import {useAppSelector} from "../../../store/hooks";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useEmailVerification} from "../../../user/hooks/use_email_verification";
import {checkHasVerifiedPhone} from "../../../user/utils/check_has_verified_phone";
import {ViewType} from "../../../view_type/ViewType";
import {trackClassicMultiLeadClose, trackClassicMultiLeadStart} from "../../components/multi_lead/tracking/track_classic_multi_lead_events";
import {
    MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_ALL_REGIONS,
    MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_WARSAW
} from "../../constants/multi_lead/application_multi_lead_max_distance";
import {WARSAW_REGION_ID} from "../../constants/multi_lead/application_multi_lead_region_ids";
import {hitMultileadSendButton} from "../../tracking/lead/hit_multilead_send_button";
import {IApplicationFormValues} from "../../types/form/IApplicationFormValues";
import {ApplicationMultiLeadRecommendationType} from "../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {IApplicationMultiLeadSimpleRecommendationsProps} from "../../types/multi_lead/IApplicationMultiLeadSimpleRecommendationsProps";
import {mapApplicationDetailToRecommendedProperty} from "../../utils/multi_lead/map_application_detail_to_recommended_property";
import {useApplicationPropertyPriceEstimatedByArea} from "../use_application_property_price_estimated_by_area";
import {useLeadData} from "../use_lead_data";
import {useLeadRequiredParams} from "../use_lead_required_params";
import {useMultiLeadFormSend} from "./use_application_multi_lead_form_send";
import {useMultiLeadRecommendations} from "./use_application_multi_lead_recommendations";
import {useRecommendationsSelection} from "./use_application_multi_lead_recommendations_selection";
import {useMultiLeadReport} from "./use_multi_lead_report";

const MAX_SHOWN_CURLED = 3;

export type IApplicationMultiLeadSimpleRecommendationsFormValues = Omit<IApplicationFormValues, "marketing_sms"> &
    Partial<Pick<IApplicationFormValues, "marketing_sms">>;

export type ApplicationMultiLeadFieldErrors = Record<string, string | string[]>;

interface IApplicationMultiLeadSubmitHandlers {
    onFieldErrors: (fieldErrors: ApplicationMultiLeadFieldErrors) => void;
    onSettled?: () => void;
}

interface IApplicationMultiLeadValidationError {
    appError: {
        fieldErrors: ApplicationMultiLeadFieldErrors;
        nonFieldErrors: string[] | string;
    };
}

const isApplicationMultiLeadValidationError = (error: unknown): error is IApplicationMultiLeadValidationError =>
    typeof error === "object" &&
    error !== null &&
    "appError" in error &&
    Boolean((error as Partial<IApplicationMultiLeadValidationError>).appError?.fieldErrors);

interface IUseApplicationMultiLeadSimpleRecommendationsParams extends IApplicationMultiLeadSimpleRecommendationsProps {
    sendButtonText: string;
}

const calcMaxRecommendations = (recommendations: IRecommendedProperty[], originApplicationRegionCityID: number | undefined) => {
    const maxRecommendationDistance =
        originApplicationRegionCityID && originApplicationRegionCityID === WARSAW_REGION_ID
            ? MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_WARSAW
            : MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_ALL_REGIONS;

    const baseRecommendationsGroupAmount = recommendations.filter((recommendation) => recommendation.distance < maxRecommendationDistance).length;

    return baseRecommendationsGroupAmount > 0 && baseRecommendationsGroupAmount < MAX_SHOWN_CURLED ? baseRecommendationsGroupAmount : MAX_SHOWN_CURLED;
};

export const useApplicationMultiLeadSimpleRecommendations = (params: IUseApplicationMultiLeadSimpleRecommendationsParams) => {
    const {sendButtonText, ...props} = params;
    const {
        showOriginalOffer,
        onModalClose,
        originalApplicationFormValues,
        originalApplicationResponse,
        recommendationType,
        onSuccessfulSubmit,
        customGtmTracking,
        disableDefaultSelection,
        amplitudeAuthPlacement
    } = props;

    const dispatch = useDispatch();
    const {vendorId, offerId, propertyId} = useLeadRequiredParams();
    const applicationData = useLeadData({vendorId, offerId: offerId || undefined, propertyId: propertyId || undefined});
    const viewType = useAppSelector((store) => store.viewType.current);
    const originalApplicationGeoPoint = applicationData?.offer?.detail?.geo_point;
    const originalApplicationRegionCityID = applicationData?.offer?.detail?.region?.stats?.region_type_city?.id;
    const originalApplicationCountry = applicationData?.offer?.detail?.region.country;
    const {modalOpen, checkboxChange, mapPinClick} = useMultiLeadReport(customGtmTracking);
    const {recommendedOffers, recommendedProperties, areRecommendationsLoading} = useMultiLeadRecommendations({
        propertyId: applicationData?.property?.detail?.id,
        offerId: applicationData?.offer?.detail?.id
    });
    const {regionEstimatedPriceSummary, requestState: regionEstimatedPriceSummaryRequestState} = useRegionEstimatedPrice({
        region: applicationData?.offer?.detail?.region
    });
    const {estimatedPriceInfoText} = useApplicationPropertyPriceEstimatedByArea({
        regionEstimatedPriceSummary,
        price: originalApplicationResponse?.property_price,
        isPriceRevealed: !!originalApplicationResponse?.property_price,
        area: applicationData?.property?.detail?.area,
        country: originalApplicationCountry
    });
    const {data: userProfile} = useGetUserProfileQuery();
    const [showPropertiesList, setShowPropertiesList] = useState(true);
    const [isMultiLeadSubmitting, setIsMultiLeadSubmitting] = useState(false);
    const [applicationsApiError, setApplicationsApiError] = useState<string | null>(null);
    const amplitudeStartTrackedRef = useRef(false);
    const hasSuccessfulAmplitudeSubmitRef = useRef(false);
    const amplitudeCloseTrackedRef = useRef(false);
    const amplitudeRecommendationsRef = useRef<(IRecommendedProperty | IRecommendedOffer)[]>(recommendedProperties);
    const amplitudeRecommendationTypeRef = useRef(recommendationType);
    const hasVerifiedPhone = checkHasVerifiedPhone(userProfile);
    // TODO: Bring back if we'll need to set some defaults again - CU-8695y5kux
    const maxRecommendationsShownOnCurl = calcMaxRecommendations(recommendedProperties, originalApplicationRegionCityID);

    const {selectedRecommendationsCount, selectedRecommendations, toggleRecommendation} = useRecommendationsSelection({
        recommendedOffers,
        recommendedProperties,
        maxRecommendationsShown: disableDefaultSelection ? 0 : maxRecommendationsShownOnCurl,
        recommendationType
    });

    const isSendApplicationDisabled = selectedRecommendationsCount === 0;
    const totalOffers = recommendedProperties.length + (showOriginalOffer ? 1 : 0);
    const amplitudeRecommendations = recommendationType === ApplicationMultiLeadRecommendationType.PROPERTY ? recommendedProperties : recommendedOffers;
    amplitudeRecommendationsRef.current = amplitudeRecommendations;
    amplitudeRecommendationTypeRef.current = recommendationType;

    const trackAmplitudeClose = () => {
        if (amplitudeStartTrackedRef.current && !hasSuccessfulAmplitudeSubmitRef.current && !amplitudeCloseTrackedRef.current) {
            amplitudeCloseTrackedRef.current = true;
            trackClassicMultiLeadClose(amplitudeRecommendationTypeRef.current, amplitudeRecommendationsRef.current, amplitudeAuthPlacement);
        }
    };

    useEffect(() => {
        recommendedOffers.slice(3).map((el) => toggleRecommendation(el.id));
        return () => {
            // Clear recommended properties, offers and form values - we don't need this data anymore
            dispatch(resetRecommendedProperties());
            dispatch(resetRecommendedOffers());
        };
    }, []);

    useEffect(() => {
        if (!areRecommendationsLoading && totalOffers > 1) {
            modalOpen(totalOffers, sendButtonText);
        }
    }, [areRecommendationsLoading, totalOffers, sendButtonText]);

    useEffect(() => {
        if (areRecommendationsLoading || amplitudeStartTrackedRef.current || !amplitudeRecommendations.length) {
            return;
        }

        amplitudeStartTrackedRef.current = true;
        trackClassicMultiLeadStart(recommendationType, amplitudeRecommendations, amplitudeAuthPlacement);
    }, [amplitudeRecommendations, areRecommendationsLoading, recommendationType]);

    useEffect(() => () => trackAmplitudeClose(), []);

    const {sendMultiLeadForm} = useMultiLeadFormSend({
        ...props,
        castRtkError: castRtkErrorForFormik,
        customGtmTracking: customGtmTracking,
        sendTrackingData: {selectedCount: selectedRecommendationsCount, totalOffers, buttonText: sendButtonText}
    });
    const {props: emailNotificationProps, validateEmailOnce} = useEmailVerification();

    const handleSentLeads = (sentRecommendedLeads: Awaited<ReturnType<typeof sendMultiLeadForm>>) => {
        if (sentRecommendedLeads.length) {
            hasSuccessfulAmplitudeSubmitRef.current = true;
        }

        onSuccessfulSubmit && onSuccessfulSubmit(sentRecommendedLeads);
    };

    const handleFieldErrors = (fieldErrors: ApplicationMultiLeadFieldErrors) => {
        setIsMultiLeadSubmitting(false);
        const applicationsError = fieldErrors.applications;
        if (applicationsError) {
            setApplicationsApiError(Array.isArray(applicationsError) ? applicationsError[0] : applicationsError);
        }
    };

    const submitApplications = async (formValues: IApplicationMultiLeadSimpleRecommendationsFormValues, handlers: IApplicationMultiLeadSubmitHandlers) => {
        if (formValues?.email && !(await validateEmailOnce(formValues?.email))) {
            return;
        }

        setIsMultiLeadSubmitting(true);

        const formValuesWithSms = {...formValues, marketing_sms: formValues.marketing_sms || false};
        const mainPropertyDetail = applicationData?.property?.detail;
        const mainOfferDetail = applicationData?.offer?.detail;
        const isMainPropertySelected = showOriginalOffer && mainPropertyDetail && mainOfferDetail && selectedRecommendations.includes(mainPropertyDetail.id);

        void sendMultiLeadForm(
            isMainPropertySelected
                ? {
                      recommendedProperties: [
                          mapApplicationDetailToRecommendedProperty(mainPropertyDetail, mainOfferDetail),
                          ...recommendedProperties.filter((p) => selectedRecommendations.includes(p.id))
                      ],
                      formValues: formValuesWithSms
                  }
                : {selectedRecommendations, formValues: formValuesWithSms}
        )
            .then((sentRecommendedLeads) => {
                if (sentRecommendedLeads) {
                    handleSentLeads(sentRecommendedLeads);
                }
            })
            .catch((error: unknown) => {
                if (!isApplicationMultiLeadValidationError(error)) {
                    setIsMultiLeadSubmitting(false);
                    return;
                }

                handlers.onFieldErrors(error.appError.fieldErrors);
                handleFieldErrors(error.appError.fieldErrors);
            })
            .finally(() => handlers.onSettled?.());
    };

    const handleChange = (viewType: ViewType, recommendationId: number) => {
        setApplicationsApiError(null);
        const isCheckboxSelected = toggleRecommendation(recommendationId);
        // GTM & Algolytics events
        checkboxChange(recommendationType, viewType, isCheckboxSelected, recommendationId);
    };

    const handlePinClick = (viewType: ViewType | null, recommendationId: number) => {
        if (!(recommendationType in ApplicationMultiLeadRecommendationType)) {
            throw new Error("Unknown recommendation type");
        }

        setApplicationsApiError(null);
        const isCheckboxSelected = toggleRecommendation(recommendationId);
        mapPinClick(viewType, recommendationId, isCheckboxSelected);
    };

    const onSendClick = () => {
        hitMultileadSendButton();
    };

    const handleClose = () => {
        trackAmplitudeClose();
        onModalClose(totalOffers);
    };

    const isSingleLeadSent = Boolean(originalApplicationResponse);
    const isLeadPropertyAvailable = Boolean(applicationData?.property?.detail?.id);
    const isFormFilled = !!originalApplicationFormValues.email && !!originalApplicationFormValues.name && !!originalApplicationFormValues.phone.number;
    const initialValues: IApplicationMultiLeadSimpleRecommendationsFormValues = isFormFilled
        ? originalApplicationFormValues
        : {
              ...originalApplicationFormValues,
              email: userProfile?.email || "",
              name: userProfile?.name || "",
              phone: getPhoneDataFromString(userProfile?.phone || ""),
              marketing_email: typeof userProfile?.email_marketing_consent === "boolean" ? userProfile?.email_marketing_consent : true,
              marketing_sms: typeof userProfile?.phone_marketing_consent === "boolean" ? userProfile?.phone_marketing_consent : undefined
          };

    return {
        applicationData,
        viewType,
        originalApplicationGeoPoint,
        originalApplicationCountry,
        recommendedProperties,
        recommendedOffers,
        estimatedPriceInfoText,
        regionEstimatedPriceSummaryRequestState,
        hasVerifiedPhone,
        showPropertiesList,
        setShowPropertiesList,
        isMultiLeadSubmitting,
        applicationsApiError,
        selectedRecommendations,
        isSendApplicationDisabled,
        emailNotificationProps,
        isSingleLeadSent,
        isLeadPropertyAvailable,
        initialValues,
        submitApplications,
        handleChange,
        handlePinClick,
        onSendClick,
        handleClose
    };
};
import {useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";

import {castRtkErrorForFormik} from "@pg-mono/react-hook-forms-utils";
import {getPhoneDataFromString} from "@pg-mono/string-utils";

import {resetRecommendedOffers} from "../../../offer/detail/actions/fetch_recommended_offers";
import {IRecommendedOffer} from "../../../offer/types/IRecommendedOffer";
import {resetRecommendedProperties} from "../../../property/actions/fetch_recommended_properties";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {useRegionEstimatedPrice} from "../../../region/hooks/use_region_estimated_price";
import {useAppSelector} from "../../../store/hooks";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useEmailVerification} from "../../../user/hooks/use_email_verification";
import {checkHasVerifiedPhone} from "../../../user/utils/check_has_verified_phone";
import {ViewType} from "../../../view_type/ViewType";
import {trackClassicMultiLeadClose, trackClassicMultiLeadStart} from "../../components/multi_lead/tracking/track_classic_multi_lead_events";
import {
    MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_ALL_REGIONS,
    MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_WARSAW
} from "../../constants/multi_lead/application_multi_lead_max_distance";
import {WARSAW_REGION_ID} from "../../constants/multi_lead/application_multi_lead_region_ids";
import {hitMultileadSendButton} from "../../tracking/lead/hit_multilead_send_button";
import {IApplicationFormValues} from "../../types/form/IApplicationFormValues";
import {ApplicationMultiLeadRecommendationType} from "../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {IApplicationMultiLeadSimpleRecommendationsProps} from "../../types/multi_lead/IApplicationMultiLeadSimpleRecommendationsProps";
import {mapApplicationDetailToRecommendedProperty} from "../../utils/multi_lead/map_application_detail_to_recommended_property";
import {useApplicationPropertyPriceEstimatedByArea} from "../use_application_property_price_estimated_by_area";
import {useLeadData} from "../use_lead_data";
import {useLeadRequiredParams} from "../use_lead_required_params";
import {useMultiLeadFormSend} from "./use_application_multi_lead_form_send";
import {useMultiLeadRecommendations} from "./use_application_multi_lead_recommendations";
import {useRecommendationsSelection} from "./use_application_multi_lead_recommendations_selection";
import {useMultiLeadReport} from "./use_multi_lead_report";

const MAX_SHOWN_CURLED = 3;

export type IApplicationMultiLeadSimpleRecommendationsFormValues = Omit<IApplicationFormValues, "marketing_sms"> &
    Partial<Pick<IApplicationFormValues, "marketing_sms">>;

export type ApplicationMultiLeadFieldErrors = Record<string, string | string[]>;

interface IApplicationMultiLeadSubmitHandlers {
    onFieldErrors: (fieldErrors: ApplicationMultiLeadFieldErrors) => void;
    onSettled?: () => void;
}

interface IApplicationMultiLeadValidationError {
    appError: {
        fieldErrors: ApplicationMultiLeadFieldErrors;
        nonFieldErrors: string[] | string;
    };
}

const isApplicationMultiLeadValidationError = (error: unknown): error is IApplicationMultiLeadValidationError =>
    typeof error === "object" &&
    error !== null &&
    "appError" in error &&
    Boolean((error as Partial<IApplicationMultiLeadValidationError>).appError?.fieldErrors);

interface IUseApplicationMultiLeadSimpleRecommendationsParams extends IApplicationMultiLeadSimpleRecommendationsProps {
    sendButtonText: string;
}

const calcMaxRecommendations = (recommendations: IRecommendedProperty[], originApplicationRegionCityID: number | undefined) => {
    const maxRecommendationDistance =
        originApplicationRegionCityID && originApplicationRegionCityID === WARSAW_REGION_ID
            ? MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_WARSAW
            : MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_ALL_REGIONS;

    const baseRecommendationsGroupAmount = recommendations.filter((recommendation) => recommendation.distance < maxRecommendationDistance).length;

    return baseRecommendationsGroupAmount > 0 && baseRecommendationsGroupAmount < MAX_SHOWN_CURLED ? baseRecommendationsGroupAmount : MAX_SHOWN_CURLED;
};

export const useApplicationMultiLeadSimpleRecommendations = (params: IUseApplicationMultiLeadSimpleRecommendationsParams) => {
    const {sendButtonText, ...props} = params;
    const {
        showOriginalOffer,
        onModalClose,
        originalApplicationFormValues,
        originalApplicationResponse,
        recommendationType,
        onSuccessfulSubmit,
        customGtmTracking,
        disableDefaultSelection,
        amplitudeAuthPlacement
    } = props;

    const dispatch = useDispatch();
    const {vendorId, offerId, propertyId} = useLeadRequiredParams();
    const applicationData = useLeadData({vendorId, offerId: offerId || undefined, propertyId: propertyId || undefined});
    const viewType = useAppSelector((store) => store.viewType.current);
    const originalApplicationGeoPoint = applicationData?.offer?.detail?.geo_point;
    const originalApplicationRegionCityID = applicationData?.offer?.detail?.region?.stats?.region_type_city?.id;
    const originalApplicationCountry = applicationData?.offer?.detail?.region.country;
    const {modalOpen, checkboxChange, mapPinClick} = useMultiLeadReport(customGtmTracking);
    const {recommendedOffers, recommendedProperties, areRecommendationsLoading} = useMultiLeadRecommendations({
        propertyId: applicationData?.property?.detail?.id,
        offerId: applicationData?.offer?.detail?.id
    });
    const {regionEstimatedPriceSummary, requestState: regionEstimatedPriceSummaryRequestState} = useRegionEstimatedPrice({
        region: applicationData?.offer?.detail?.region
    });
    const {estimatedPriceInfoText} = useApplicationPropertyPriceEstimatedByArea({
        regionEstimatedPriceSummary,
        price: originalApplicationResponse?.property_price,
        isPriceRevealed: !!originalApplicationResponse?.property_price,
        area: applicationData?.property?.detail?.area,
        country: originalApplicationCountry
    });
    const {data: userProfile} = useGetUserProfileQuery();
    const [showPropertiesList, setShowPropertiesList] = useState(true);
    const [isMultiLeadSubmitting, setIsMultiLeadSubmitting] = useState(false);
    const [applicationsApiError, setApplicationsApiError] = useState<string | null>(null);
    const amplitudeStartTrackedRef = useRef(false);
    const hasSuccessfulAmplitudeSubmitRef = useRef(false);
    const amplitudeCloseTrackedRef = useRef(false);
    const amplitudeRecommendationsRef = useRef<(IRecommendedProperty | IRecommendedOffer)[]>(recommendedProperties);
    const amplitudeRecommendationTypeRef = useRef(recommendationType);
    const hasVerifiedPhone = checkHasVerifiedPhone(userProfile);
    // TODO: Bring back if we'll need to set some defaults again - CU-8695y5kux
    const maxRecommendationsShownOnCurl = calcMaxRecommendations(recommendedProperties, originalApplicationRegionCityID);

    const {selectedRecommendationsCount, selectedRecommendations, toggleRecommendation} = useRecommendationsSelection({
        recommendedOffers,
        recommendedProperties,
        maxRecommendationsShown: disableDefaultSelection ? 0 : maxRecommendationsShownOnCurl,
        recommendationType
    });

    const isSendApplicationDisabled = selectedRecommendationsCount === 0;
    const totalOffers = recommendedProperties.length + (showOriginalOffer ? 1 : 0);
    const amplitudeRecommendations = recommendationType === ApplicationMultiLeadRecommendationType.PROPERTY ? recommendedProperties : recommendedOffers;
    amplitudeRecommendationsRef.current = amplitudeRecommendations;
    amplitudeRecommendationTypeRef.current = recommendationType;

    const trackAmplitudeClose = () => {
        if (amplitudeStartTrackedRef.current && !hasSuccessfulAmplitudeSubmitRef.current && !amplitudeCloseTrackedRef.current) {
            amplitudeCloseTrackedRef.current = true;
            trackClassicMultiLeadClose(amplitudeRecommendationTypeRef.current, amplitudeRecommendationsRef.current, amplitudeAuthPlacement);
        }
    };

    useEffect(() => {
        recommendedOffers.slice(3).map((el) => toggleRecommendation(el.id));
        return () => {
            // Clear recommended properties, offers and form values - we don't need this data anymore
            dispatch(resetRecommendedProperties());
            dispatch(resetRecommendedOffers());
        };
    }, []);

    useEffect(() => {
        if (!areRecommendationsLoading && totalOffers > 1) {
            modalOpen(totalOffers, sendButtonText);
        }
    }, [areRecommendationsLoading, totalOffers, sendButtonText]);

    useEffect(() => {
        if (areRecommendationsLoading || amplitudeStartTrackedRef.current || !amplitudeRecommendations.length) {
            return;
        }

        amplitudeStartTrackedRef.current = true;
        trackClassicMultiLeadStart(recommendationType, amplitudeRecommendations, amplitudeAuthPlacement);
    }, [amplitudeRecommendations, areRecommendationsLoading, recommendationType]);

    useEffect(() => () => trackAmplitudeClose(), []);

    const {sendMultiLeadForm} = useMultiLeadFormSend({
        ...props,
        castRtkError: castRtkErrorForFormik,
        customGtmTracking: customGtmTracking,
        sendTrackingData: {selectedCount: selectedRecommendationsCount, totalOffers, buttonText: sendButtonText}
    });
    const {props: emailNotificationProps, validateEmailOnce} = useEmailVerification();

    const handleSentLeads = (sentRecommendedLeads: Awaited<ReturnType<typeof sendMultiLeadForm>>) => {
        if (sentRecommendedLeads.length) {
            hasSuccessfulAmplitudeSubmitRef.current = true;
        }

        onSuccessfulSubmit && onSuccessfulSubmit(sentRecommendedLeads);
    };

    const handleFieldErrors = (fieldErrors: ApplicationMultiLeadFieldErrors) => {
        setIsMultiLeadSubmitting(false);
        const applicationsError = fieldErrors.applications;
        if (applicationsError) {
            setApplicationsApiError(Array.isArray(applicationsError) ? applicationsError[0] : applicationsError);
        }
    };

    const submitApplications = async (formValues: IApplicationMultiLeadSimpleRecommendationsFormValues, handlers: IApplicationMultiLeadSubmitHandlers) => {
        if (formValues?.email && !(await validateEmailOnce(formValues?.email))) {
            return;
        }

        setIsMultiLeadSubmitting(true);

        const formValuesWithSms = {...formValues, marketing_sms: formValues.marketing_sms || false};
        const mainPropertyDetail = applicationData?.property?.detail;
        const mainOfferDetail = applicationData?.offer?.detail;
        const isMainPropertySelected = showOriginalOffer && mainPropertyDetail && mainOfferDetail && selectedRecommendations.includes(mainPropertyDetail.id);

        void sendMultiLeadForm(
            isMainPropertySelected
                ? {
                      recommendedProperties: [
                          mapApplicationDetailToRecommendedProperty(mainPropertyDetail, mainOfferDetail),
                          ...recommendedProperties.filter((p) => selectedRecommendations.includes(p.id))
                      ],
                      formValues: formValuesWithSms
                  }
                : {selectedRecommendations, formValues: formValuesWithSms}
        )
            .then((sentRecommendedLeads) => {
                if (sentRecommendedLeads) {
                    handleSentLeads(sentRecommendedLeads);
                }
            })
            .catch((error: unknown) => {
                if (!isApplicationMultiLeadValidationError(error)) {
                    setIsMultiLeadSubmitting(false);
                    return;
                }

                handlers.onFieldErrors(error.appError.fieldErrors);
                handleFieldErrors(error.appError.fieldErrors);
            })
            .finally(() => handlers.onSettled?.());
    };

    const handleChange = (viewType: ViewType, recommendationId: number) => {
        setApplicationsApiError(null);
        const isCheckboxSelected = toggleRecommendation(recommendationId);
        // GTM & Algolytics events
        checkboxChange(recommendationType, viewType, isCheckboxSelected, recommendationId);
    };

    const handlePinClick = (viewType: ViewType | null, recommendationId: number) => {
        if (!(recommendationType in ApplicationMultiLeadRecommendationType)) {
            throw new Error("Unknown recommendation type");
        }

        setApplicationsApiError(null);
        const isCheckboxSelected = toggleRecommendation(recommendationId);
        mapPinClick(viewType, recommendationId, isCheckboxSelected);
    };

    const onSendClick = () => {
        hitMultileadSendButton();
    };

    const handleClose = () => {
        trackAmplitudeClose();
        onModalClose(totalOffers);
    };

    const isSingleLeadSent = Boolean(originalApplicationResponse);
    const isLeadPropertyAvailable = Boolean(applicationData?.property?.detail?.id);
    const isFormFilled = !!originalApplicationFormValues.email && !!originalApplicationFormValues.name && !!originalApplicationFormValues.phone.number;
    const initialValues: IApplicationMultiLeadSimpleRecommendationsFormValues = isFormFilled
        ? originalApplicationFormValues
        : {
              ...originalApplicationFormValues,
              email: userProfile?.email || "",
              name: userProfile?.name || "",
              phone: getPhoneDataFromString(userProfile?.phone || ""),
              marketing_email: typeof userProfile?.email_marketing_consent === "boolean" ? userProfile?.email_marketing_consent : true,
              marketing_sms: typeof userProfile?.phone_marketing_consent === "boolean" ? userProfile?.phone_marketing_consent : undefined
          };

    return {
        applicationData,
        viewType,
        originalApplicationGeoPoint,
        originalApplicationCountry,
        recommendedProperties,
        recommendedOffers,
        estimatedPriceInfoText,
        regionEstimatedPriceSummaryRequestState,
        hasVerifiedPhone,
        showPropertiesList,
        setShowPropertiesList,
        isMultiLeadSubmitting,
        applicationsApiError,
        selectedRecommendations,
        isSendApplicationDisabled,
        emailNotificationProps,
        isSingleLeadSent,
        isLeadPropertyAvailable,
        initialValues,
        submitApplications,
        handleChange,
        handlePinClick,
        onSendClick,
        handleClose
    };
};
